import React, { useState, useEffect } from "react";
import { t } from "../../../../utils/i18n";
import {
  calculateUsdAmount,
  sanitizeInput,
  formatProductTag,
} from "../Helper";
import * as ImportedCurrencies from "../currencies.json";

// Import new components
import PaymentFormFields from "./PaymentFormFields";
import ProductDetailsForm from "./ProductDetailsForm";
import GoHighLevelFormSection from "./GoHighLevelFormSection";
import SubmitButton from "./SubmitButton";
import useFormData from "./useFormData";
import useFormValidation from "./useFormValidation";
import useFormSubmission from "./useFormSubmission";

const LinkForm = ({
  exchangeRates,
  isLoadingRates,
  storeId,
  walletAddress,
  integration,
  showSuccessModal,
}) => {
  const CURRENCIES = ImportedCurrencies.default || ImportedCurrencies;

  // Use custom hooks
  const initialFormData = useFormData(integration);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [showGhlApiKeyTooltip, setShowGhlApiKeyTooltip] = useState(false);
  const [formTouched, setFormTouched] = useState(false);

  const { validateForm, validateField } = useFormValidation(
    formData,
    integration
  );
  const { handleSubmit: submitForm } = useFormSubmission();

  useEffect(() => {
    const allTags = [
      "not_paid",
      "paid",
      formData.product_name ? formatProductTag(formData.product_name) : null,
      ...formData.custom_tags
        .filter((tag) => tag.trim() !== "")
        .map((tag) => formatProductTag(tag)),
    ].filter(Boolean);

    // Only update if tags have actually changed
    const currentTags = formData.gohighlevel_tags || [];
    const tagsChanged =
      allTags.length !== currentTags.length ||
      allTags.some((tag, index) => tag !== currentTags[index]);

    if (tagsChanged) {
      setFormData((prev) => ({
        ...prev,
        gohighlevel_tags: allTags,
      }));
    }
  }, [formData.product_name, formData.custom_tags]);

  useEffect(() => {
    if (formTouched) {
      const timeoutId = setTimeout(() => {
        const validationErrors = validateForm();
        setErrors(validationErrors);
      }, 300); // Debounce validation to prevent rapid updates

      return () => clearTimeout(timeoutId);
    }
  }, [formData, formTouched]); // Removed validateForm from dependencies

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (!formTouched) {
      setFormTouched(true);
    }

    if (name === "amount") {
      const regex = /^[0-9]*\.?[0-9]*$/;
      if (value === "" || regex.test(value)) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      return; // Early return to prevent further processing
    }

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    if (name.startsWith("custom_tag_")) {
      const index = parseInt(name.split("_")[2]);
      const newCustomTags = [...formData.custom_tags];
      newCustomTags[index] = sanitizeInput(value);
      setFormData((prev) => ({ ...prev, custom_tags: newCustomTags }));
      return;
    }

    if (
      [
        "product_name",
        "description",
        "pay_button_cta",
        "pay_button_note",
      ].includes(name)
    ) {
      const processedValue = sanitizeInput(value);
      setFormData((prev) => ({ ...prev, [name]: processedValue }));
      return;
    }

    // Default case for other fields
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const fieldError = validateField(name, formData[name]);

    if (errors[name] !== fieldError) {
      setErrors((prev) => ({
        ...prev,
        [name]: fieldError,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    await submitForm({
      formData,
      integration,
      storeId,
      walletAddress,
      exchangeRates,
      showSuccessModal,
      validateForm,
      setCreating,
      setErrors,
    });
  };

  return (
    <div className="link-generator-section">
      <h2 className="doc-integration-title">{t("store_integrations.widget.create_link_title")}</h2>
      <p className="generator-description">
        {t("store_integrations.widget.create_link_desc")}
      </p>

      <form onSubmit={handleSubmit} noValidate>
        <PaymentFormFields
          formData={formData}
          handleInputChange={handleInputChange}
          handleBlur={handleBlur}
          errors={errors}
          currencies={CURRENCIES}
          exchangeRates={exchangeRates}
          isLoadingRates={isLoadingRates}
          calculateUsdAmount={calculateUsdAmount}
        />

        <ProductDetailsForm
          formData={formData}
          handleInputChange={handleInputChange}
          handleBlur={handleBlur}
          errors={errors}
        />

        <GoHighLevelFormSection
          formData={formData}
          handleInputChange={handleInputChange}
          handleBlur={handleBlur}
          errors={errors}
          showGhlApiKeyTooltip={showGhlApiKeyTooltip}
          setShowGhlApiKeyTooltip={setShowGhlApiKeyTooltip}
          isEditing={!!integration?.id}
        />

        <SubmitButton
          creating={creating}
          integration={integration}
          errors={errors}
        />
      </form>
    </div>
  );
};

export default LinkForm;
